import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ILogReenvio, IFilter, IQueryParam, IPopulate } from 'modelos/src';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../../../auxiliares/tabla/tabla.component';
import { fechaCelda, haceCelda } from '../../../../../auxiliares/tabla/celdas';
import { LogReenviosService } from '../../../../../auxiliares/servicios/http/log-reenvios.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { VerBodyDialogComponent } from '../ver-body-dialog/ver-body-dialog.component';

@Component({
  selector: 'app-listado-log-reenvios',
  templateUrl: './listado-log-reenvios.component.html',
  standalone: false,
})
export class ListadoLogReenviosComponent implements OnInit, OnDestroy {
  private lista2Service = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(LogReenviosService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);

  //// Tabla nueva
  public name = 'ListadoLogReenviosComponent';
  public datos?: ILogReenvio[];
  public datos$?: Subscription;
  public totalCount = 0;
  public columnas?: IColumnas<ILogReenvio>[];

  public search: IRegExpSearch = {
    fields: ['body'],
  };

  // Vehículo y Activo filtran por su tracker: los logs guardan en `idEntidad`
  // el tracker (o la alarma), nunca el activo. Con `_id` el filtro nunca
  // matcheaba nada.
  private vehiculo: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidad',
    },
    label: 'Vehículo',
    selectLabel: 'identificacion',
    selectValue: 'idTracker',
    tipo: 'ngselect-async',
    asyncFunction: 'vehiculos',
    searchOn: ['identificacion'],
  };
  private activo: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidad',
    },
    label: 'Activo',
    selectLabel: 'identificacion',
    selectValue: 'idTracker',
    tipo: 'ngselect-async',
    asyncFunction: 'activos',
    searchOn: ['identificacion'],
  };
  private alarma: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidad',
    },
    label: 'Alarma',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'dispositivosAlarmas',
    searchOn: ['nombre'],
  };
  public tracker: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idEntidad',
    },
    label: 'Tracker',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'trackers',
    searchOn: ['nombre'],
  };
  /**
   * Tipo de reenvío. Se filtra por `protocolo`, que es lo que cada integración
   * deja escrito en el log: los reenvíos por socket guardan el protocolo real
   * (TCP/UDP) y los que van por HTTP guardan el nombre de la integración.
   *
   * Límite conocido, hasta que el log guarde el método de reenvío: `TCP`/`UDP`
   * mezclan el reenvío Básico con Caessat y Cersat, que también van por
   * socket y guardan su protocolo.
   *
   * Logictracker y Objetivo AVL escriben su nombre desde la versión que lo
   * agregó; sus logs anteriores quedan en "Sin especificar", que usa $in
   * porque el campo puede faltar o venir vacío.
   */
  private tipoReenvio: IFiltroTabla = {
    elementos: [
      { label: 'Objetivo AVL (Policía de la Ciudad)', value: 'Objetivo AVL' },
      { label: 'Soflex', value: 'Soflex' },
      { label: 'Iron Tracking', value: 'Iron Tracking' },
      { label: 'Logictracker', value: 'Logictracker' },
      { label: 'TCP (Básico / Caessat / Cersat)', value: 'TCP' },
      { label: 'UDP (Básico / Caessat / Cersat)', value: 'UDP' },
      {
        label: 'Sin especificar (logs viejos)',
        value: { $in: [null, ''] },
      },
    ],
    filter: {
      field: 'protocolo',
    },
    label: 'Tipo de reenvío',
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'value',
    compararPorJson: true,
  };
  private cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad reenviada: el cliente elegido y
  // toda su descendencia. Es el que sirve cuando los trackers están repartidos
  // en clientes hijos y se quiere ver el reenvío del municipio entero.
  private ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  private rango: IFiltroTabla = {
    filter: {
      field: 'fecha',
    },
    label: 'Rango de fechas',
    tipo: 'dateRange',
  };
  private fecha: IFiltroTabla = {
    filter: {
      field: 'fecha',
    },
    label: 'Fechas',
    tipo: 'date',
  };

  public filtros: IFiltroTabla[] = [
    this.tipoReenvio,
    this.cliente,
    this.ancestro,
    this.vehiculo,
    this.tracker,
    this.activo,
    this.alarma,
    this.rango,
    this.fecha,
  ];

  private filter: IFilter<ILogReenvio> = {};
  private populate: IPopulate = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'dispositivoAlarma',
      select: 'nombre',
    },
    {
      path: 'tracker',
      select: 'nombre',
    },
  ];

  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: '-fecha',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
    excludeTotalCount: true,
  };

  public async listar(): Promise<void> {
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2Service
      .logReenvios(this.query, 30)
      .subscribe((data) => {
        this.totalCount = HelperService.generarTotalCount(
          this.query,
          data.datos.length,
        );
        this.datos = data.datos;
      });
  }

  // Acciones

  public async editar(logreenvio: ILogReenvio) {
    this.navigator.navigate(['editar', logreenvio._id], {
      relativeTo: this.route,
    });
  }

  public async eliminar(logreenvio: ILogReenvio) {
    const res = await this.dialogService.confirm(
      'Eliminar log de reenvío',
      `¿Querés eliminar el log de reenvío?`,
    );
    if (!res) return;
    try {
      await this.service.delete(logreenvio._id!);
      this.helper.notifSuccess('Log de reenvío eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  /** Detalle completo del reenvío: destino, método, headers y body. */
  public verDetalle(log: ILogReenvio): void {
    this.formularios.abrir(VerBodyDialogComponent, {
      tamano: 'mediano',
      data: log,
      cerrarAfuera: true,
    });
  }

  // Tabla

  private setColumnas() {
    this.columnas = [
      // Fecha
      {
        header: {
          label: 'Fecha',
          sortable: true,
          sort: 'fecha',
        },
        row: {
          // Es un log: los segundos van en el tooltip
          html: (dato) => {
            const f = fechaCelda(dato.fecha);
            if (!f) return '';
            return `<span class="tabla-fecha"><span>${f}</span><span class="tabla-secundario">${haceCelda(dato.fecha)}</span></span>`;
          },
          tooltip: (dato) =>
            dato.fecha
              ? `${fechaCelda(dato.fecha)}:${String(new Date(dato.fecha).getSeconds()).padStart(2, '0')}`
              : undefined,
          noData: 'Sin fecha',
        },
      },
      // Entidad
      {
        header: {
          label: 'Entidad',
        },
        row: {
          parse(dato) {
            return dato.dispositivoAlarma?.nombre || dato.tracker?.nombre || '';
          },
          noData: 'Sin entidad',
        },
      },
      // Cliente
      {
        header: {
          label: 'Cliente',
        },
        row: {
          // `field` no resuelve rutas anidadas (la tabla hace row[field]), así
          // que el populate se lee con parse.
          parse(dato) {
            return dato.cliente?.nombre ?? '';
          },
          noData: 'Sin cliente',
        },
      },
      // Destino
      {
        header: {
          label: 'Destino',
        },
        row: {
          // Los reenvíos HTTP guardan la URL completa en `host` y no traen
          // puerto; los de socket guardan host y puerto por separado. Sin esta
          // distinción la celda mostraba "undefined ...:undefined".
          parse(dato) {
            const esUrl = /^https?:\/\//.test(dato.host ?? '');
            const destino = esUrl
              ? dato.host
              : [dato.host, dato.puerto].filter((v) => v != null).join(':');
            return [dato.protocolo, destino].filter(Boolean).join(' ');
          },
          noData: 'Sin destino',
        },
      },
      // Body
      {
        header: {
          label: 'Body',
        },
        row: {
          parse: (dato) => {
            const b = dato.body ?? '';
            return b.length > 80 ? b.slice(0, 80) + '…' : b;
          },
          noData: 'Sin body',
        },
      },
      // Ack: Recibido / No recibido; el status HTTP y el error, en el tooltip
      {
        header: {
          label: 'Ack',
        },
        row: {
          estado: (dato) => {
            // El status HTTP va en el tooltip: sin él, "code 1:" no decía si
            // el servicio había contestado 200 o un error.
            const http =
              dato.respuestaStatus != null
                ? `HTTP ${dato.respuestaStatus}`
                : '';
            return dato.ack
              ? {
                  texto: 'Recibido',
                  tono: 'ok',
                  icono: 'check_circle',
                  tooltip: http || undefined,
                }
              : {
                  texto: 'No recibido',
                  tono: 'error',
                  icono: 'cancel',
                  tooltip:
                    [http, dato.error].filter(Boolean).join(' · ') ||
                    undefined,
                };
          },
        },
      },
      // Acciones
      {
        header: {
          label: 'Acciones',
        },
        row: {
          acciones: [
            {
              tipo: 'material',
              icon: 'visibility',
              tooltip: 'Ver detalle',
              click: (dato) => this.verDetalle(dato),
            },
            {
              tipo: 'material',
              color: 'warn',
              icon: 'delete',
              tooltip: 'Eliminar',
              click: (dato) => this.eliminar(dato),
              disabled: () =>
                !this.helper.tienePermiso(
                  'Administración - Eliminar configuraciones de reenvíos',
                ),
            },
          ],
        },
      },
    ];
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.datos$?.unsubscribe();
  }
}
